
import { Component, InjectReactive, Prop, Vue } from 'vue-property-decorator';

import OrderLocationDetails from '@/components/OrderLocationDetails.vue';
import ProductsSummary from '@/components/ProductsSummary.vue';
import { OrderMethods } from '@/enums';
import BasketModule from '@/store/modules/basket';
import UserModule from '@/store/modules/user';
import { formatPhoneNumber, getOrderMethod, hideCMSContent, parseDate } from '@/utils/global.util';

@Component({
  components: {
    OrderLocationDetails,
    ProductsSummary
  }
})
export default class OrderHistory extends Vue {
  @InjectReactive('progressRef') progressRef: any;

  @Prop({ type: String, default: '' }) basketId: string;
  @Prop({ type: String, default: '' }) arrivalStatus: string;
  @Prop({ type: Number, default: null }) customerHandoffCharge: number;
  @Prop({ type: Object, default: null }) deliveryAddress: DeliveryAddress | null;
  @Prop({ type: String, default: '' }) deliveryMode: string;
  @Prop({ type: Number, default: null }) discount: number;
  @Prop({ type: Array, default: null }) fees: FeeModel[] | null;
  @Prop({ type: String, default: '' }) id: string;
  @Prop({ type: String, default: null }) instructions: string | null;
  @Prop({ type: String, default: '' }) oloId: string;
  @Prop({ type: String, default: '' }) orderRef: string;
  @Prop({ type: Array, default: null }) products: OrderProductModel[];
  @Prop({ type: String, default: '' }) readyTime: string;
  @Prop({ type: Number, default: null }) salesTax: number;
  @Prop({ type: Number, default: null }) subTotal: number;
  @Prop({ type: String, default: '' }) status: string;
  @Prop({ type: Array, default: null }) taxes: TaxModel[] | null;
  @Prop({ type: String, default: '' }) timeMode: string;
  @Prop({ type: String, default: '' }) timePlaced: string;
  @Prop({ type: Number, default: null }) tip: number;
  @Prop({ type: Number, default: null }) total: number;
  @Prop({ type: Number, default: null }) totalFees: number;
  @Prop({ type: Object, default: null }) restaurant: Restaurant;

  orderStatusPollingInterval: any;

  favoriteOrderForm: {
    description: string;
    isDefault: boolean;
  };

  favoriteOrderName: string;
  favoriteOrderSaved: boolean;

  arrivalNotificationForm: {
    message: string | null;
  }

  data() {
    return {
      orderStatusPollingInterval: null,
      favoriteOrderForm: {
        description: '',
        isDefault: false
      },
      favoriteOrderName: null,
      favoriteOrderSaved: false,
      arrivalNotificationForm: {
        message: null
      },
    }
  }

  async mounted() {
    this.$scrollTo(this.progressRef as Element);

    if (this.status !== 'Completed' && this.status !== 'Canceled') {
      this.orderStatusPollingInterval = setInterval(async () => {
        await BasketModule.checkOrderStatus(this.id);
        if (this.status === 'Completed' || this.status === 'Canceled') {
          clearInterval(this.orderStatusPollingInterval);
        }
      }, 60000);
    }
  }

  beforeDestroy() {
    if (this.orderStatusPollingInterval) {
      clearInterval(this.orderStatusPollingInterval);
    }
  }

  async submitHandler(request) {
    const {
      description,
      isDefault
    } = request;

    try {
      if (this.basketId === '') {
        return;
      }

      const favorites = await UserModule.addFavoriteOrder({ basketId: this.basketId, description, isDefault });
      this.favoriteOrderName = description;
      this.favoriteOrderSaved = true;
      this.$log.info(favorites);
    } catch (err) {
      this.$toasted.error(err.data);
    }
  }

  get isLoggedIn() {
    return UserModule.loggedIn;
  }

  get orderMethod() {
    return getOrderMethod(this.deliveryMode);
  }

  get parsedTimePlaced() {
    return parseDate(this.timePlaced);
  }

  get parsedReadyTime() {
    return parseDate(this.readyTime);
  }

  get orderProducts() {
    return this.products || null;
  }

  get orderNumber() {
    return this.oloId;
  }

  get readyText() {
    switch (this.orderMethod) {
      case 'Delivery':
        return 'delivered';
      default:
        return 'ready';
    }
  }

  get showArrivalNotification() {
    return this.arrivalStatus !== '' || false;
  }

  get orderModel() {
    return { ...this.$props };
  }

  get firstName() {
    return UserModule.firstName || BasketModule.customerFirstName;
  }

  async sendArrivalNotification(model) {
    try {
      const { message } = model;
      await BasketModule.sendArrivalNotification({ orderGuid: this.id, message });
      this.$toasted.success('The store was notified!');
    } catch (err) {
      this.$toasted.error(err.data);
    }
  }

  formatPhoneNumber(phoneNumberString) {
    return formatPhoneNumber(phoneNumberString);
  }

  async handleOrderAgain() {
    const loader = this.$loading.show();
    try {
      hideCMSContent();
      await BasketModule.createBasketFromPreviousOrder({ orderRef: this.orderRef, oloId: this.oloId });
      if (this.deliveryMode === OrderMethods.CURBSIDE) {
        await BasketModule.setHandoffMethod({ method: OrderMethods.CURBSIDE });
      }

      this.$router.push(
        {
          name: 'OrderSummary',
          params: {
            ...this.$route.params,
            location: this.restaurant.slug
          }
        });
    } catch (err) {
      this.$toasted.error(err.data);
    } finally {
      loader.hide();
    }
  }
}
